/*
 * Copyright (C) 2018 - 2023 Entgra (Pvt) Ltd, Inc - All Rights Reserved.
 *
 * Unauthorised copying/redistribution of this file, via any medium is strictly prohibited.
 *
 * Licensed under the Entgra Commercial License, Version 1.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *       https://entgra.io/licenses/entgra-commercial/1.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import React, { useContext, useEffect } from 'react';
import { DeviceDetailsContext } from '../../index';
import {
  Row,
  Col,
  Card,
  Avatar,
  Tooltip,
  Popconfirm,
  Button,
  Empty,
  notification,
} from 'antd';
import singleDeviceStyles from '../../styles.module.css';
import styles from './styles.module.css';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { handleApiError } from '../../../../../../services/utils/errorHandler';
import config from '../../../../../../../public/conf/config.json';
import { InfoCircleOutlined } from '@ant-design/icons';

const colors = [
  '#f0932b',
  '#badc58',
  '#6ab04c',
  '#22a6b3',
  '#0abde3',
  '#e84393',
  '#9b59b6',
  '#eb4d4b',
  '#3498db',
  '#f9ca24',
];

const Accounts = () => {
  const deviceDetails = useContext(DeviceDetailsContext);
  const type = deviceDetails.deviceType;
  const mainDeviceInfo = deviceDetails.device.deviceInfo;
  const accounts = mainDeviceInfo?.deviceDetailsMap?.ACCOUNTS;
  const accountsArray = accounts !== undefined ? JSON.parse(accounts) : [];

  const { t } = useTranslation();
  useEffect(() => {
    // Update the current route's key
    deviceDetails.setCurrentRoute('accounts');
  }, []);

  const onConfirmRemoveAccount = async accountForRemove => {
    const accName = accountForRemove.name;
    const accType = accountForRemove.type;
    const deviceIdentifier = deviceDetails.deviceIdentifier;
    const headers = {
      'Content-Type': 'application/json', // for remove-account operation
      'if-modified-since': null, // for pending-operation call
    };
    await axios
      .post(
        window.location.origin +
          config.serverConfig.invoker.uri +
          '/device-mgt/' +
          type +
          '/v1.0/admin/devices/remove-account',
        {
          deviceID: deviceIdentifier,
          deviceType: type,
          operation: {
            name: accName,
            type: accType,
          },
        },
        {
          headers: headers,
        },
      )
      .then(res => {
        if (res.status === 200) {
          notification.success({
            message: t('api_successMsg'),
            duration: 4,
            description: t('api_removeAccountTriggeredMsg'),
          });
        }
      })
      .catch(error => {
        handleApiError(error, t('api_errorMsg'), t);
      });
  };

  if (accountsArray.length > 0) {
    return (
      <div className={singleDeviceStyles.container}>
        <InfoCircleOutlined />
        {t('label_accountRemoveDelayDetails')}
        <Row>
          {accountsArray.map(account => {
            // Generating a color based on the application name
            const color = colors[account.name.length % 10];
            // Use the first character of the app name as the avatar's letter
            const avatarLetter = account.name.charAt(0).toUpperCase();
            return (
              <Col md={8} key={account.name}>
                <Card className={styles.appCard}>
                  <Card.Meta
                    avatar={
                      <Avatar
                        style={{
                          backgroundColor: color,
                        }}
                        shape="square"
                        size="large"
                        className={styles.avatar}
                      >
                        {avatarLetter}
                      </Avatar>
                    }
                    title={
                      <Tooltip
                        placement="topLeft"
                        title={account.name}
                        autoAdjustOverflow={true}
                      >
                        <div className={styles.description}>{account.name}</div>
                      </Tooltip>
                    }
                    description={
                      <Tooltip
                        placement="rightBottom"
                        title={account.type}
                        autoAdjustOverflow={true}
                      >
                        <div className={styles.description}>{account.type}</div>
                      </Tooltip>
                    }
                  />
                  {type === 'android' &&
                    account.applicationIdentifier !== 'io.entgra.iot.agent' &&
                    (account.name.endsWith(
                      '@android-for-work.gserviceaccount.com',
                    ) ? (
                      <Button
                        className={styles.subButtons}
                        ghost
                        disabled
                        type="default"
                        size="small"
                      >
                        {t('label_remove')}
                      </Button>
                    ) : (
                      <Tooltip
                        placement="bottom"
                        title={t('label_uninstallAcc')}
                        autoAdjustOverflow={true}
                      >
                        <Popconfirm
                          placement="topLeft"
                          title={
                            t('accountRemove_confirmTxt') + account.name + ' ? '
                          }
                          onConfirm={async () => {
                            await onConfirmRemoveAccount(account);
                          }}
                          okText={t('label_ok')}
                          cancelText={t('label_cancel')}
                        >
                          <Button
                            className={styles.subButtons}
                            type="default"
                            size="small"
                          >
                            {t('label_remove')}
                          </Button>
                        </Popconfirm>
                      </Tooltip>
                    ))}
                </Card>
              </Col>
            );
          })}
        </Row>
      </div>
    );
  }

  return (
    <div className={singleDeviceStyles.container}>
      <div>
        <Empty />
      </div>
    </div>
  );
};
export default Accounts;
